import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import {
  ArrowRight, Minus, Plus, Eye, Calendar, User, Palette, Send, Trash2, MessageCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const themes = [
  { name: 'فاتح', bg: '#FAF7F2', text: '#3d2a1f', surface: '#fff' },
  { name: 'كلاسيكي', bg: '#F5F0E8', text: '#2c2420', surface: '#FBF8F3' },
  { name: 'داكن', bg: '#1a1a2e', text: '#e0dcd4', surface: '#16213e' },
  { name: 'ليلي', bg: '#0d1117', text: '#c9d1d9', surface: '#161b22' },
];

const ArticleReadPage = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fontSize, setFontSize] = useState(18);
  const [themeIdx, setThemeIdx] = useState(1);
  const [showThemePicker, setShowThemePicker] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const bottomRef = useRef(null);
  const articleEndRef = useRef(null);

  useEffect(() => { loadArticle(); loadComments(); }, [articleId]);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      setSidebarVisible(!entry.isIntersecting);
    }, { threshold: 0.1 });
    if (articleEndRef.current) observer.observe(articleEndRef.current);
    return () => observer.disconnect();
  }, [article]);

  const loadArticle = async () => {
    try {
      const res = await api.get(`/articles/${articleId}`);
      setArticle(res.data);
    } catch { toast.error('فشل تحميل المقالة'); }
    finally { setLoading(false); }
  };

  const loadComments = async () => {
    try {
      const res = await api.get(`/articles/${articleId}/comments`);
      setComments(res.data);
    } catch (e) { console.error('Failed to load article comments:', e); }
  };

  const submitComment = async () => {
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/articles/${articleId}/comments`, { text: newComment.trim() });
      setNewComment('');
      loadComments();
      toast.success('تم إضافة التعليق');
    } catch { toast.error('فشل إضافة التعليق'); }
    finally { setSubmitting(false); }
  };

  const deleteComment = async (commentId) => {
    try {
      await api.delete(`/comments/${commentId}`);
      setComments(prev => prev.filter(c => c.id !== commentId));
      toast.success('تم حذف التعليق');
    } catch { toast.error('فشل حذف التعليق'); }
  };

  const scrollToComments = () => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const canModerate = user?.is_admin && ['مطلق', 'عضو إداري'].includes(user?.admin_role);
  const theme = themes[themeIdx];
  const isDark = themeIdx >= 2;

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  if (!article) return <div className="text-center py-20"><p className="text-muted-foreground">المقالة غير موجودة</p></div>;

  const CommentItem = ({ c }) => (
    <div className="p-3 rounded-xl border" style={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)' }} data-testid={`comment-${c.id}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold" style={{ backgroundColor: '#C19A6B22', color: '#C19A6B' }}>
            {(c.reader_name || '?')[0]}
          </div>
          <span className="font-semibold text-xs" style={{ color: theme.text }}>{c.reader_name}</span>
          <span className="text-[10px] opacity-40" style={{ color: theme.text }}>{new Date(c.created_at).toLocaleDateString('ar', { month: 'short', day: 'numeric' })}</span>
        </div>
        {canModerate && (
          <button onClick={() => deleteComment(c.id)} className="text-red-400 hover:text-red-600 transition-colors" data-testid={`delete-comment-${c.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <p className="text-xs leading-relaxed" style={{ color: theme.text, whiteSpace: 'pre-wrap' }}>{c.text}</p>
    </div>
  );

  const CommentForm = ({ compact }) => (
    <div className={compact ? "space-y-2" : "flex gap-3"} data-testid="add-comment-form">
      <textarea
        value={newComment} onChange={e => setNewComment(e.target.value)}
        placeholder="اكتب تعليقك..."
        rows={compact ? 2 : 3}
        className="w-full px-3 py-2 rounded-xl border text-sm resize-none focus:ring-2 focus:ring-primary/30"
        style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#fff', borderColor: isDark ? 'rgba(255,255,255,0.12)' : '#ddd', color: theme.text }}
        dir="rtl"
        data-testid="comment-input"
      />
      <Button onClick={submitComment} disabled={submitting || !newComment.trim()} size={compact ? "sm" : "default"} className="bg-primary hover:bg-primary/90 w-full" data-testid="submit-comment-btn">
        <Send className="w-3.5 h-3.5 ml-1" /> أرسل
      </Button>
    </div>
  );

  // For mobile PDF: use Google Docs viewer
  const getPdfViewerUrl = (url) => {
    return `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(url)}`;
  };

  return (
    <div className="min-h-screen -mx-4 sm:-mx-6 lg:-mx-8 -mt-4 sm:-mt-6 -mb-8" style={{ backgroundColor: theme.bg }} data-testid="article-read-page">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <div className="flex gap-6">
          {/* Main Article */}
          <div className="flex-1 min-w-0" style={{ transition: 'flex 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}>
            <div className="rounded-2xl overflow-hidden shadow-sm" style={{ backgroundColor: theme.surface }}>
              {/* Header */}
              <div className="p-6 sm:p-8 border-b" style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
                <div className="flex items-center justify-between mb-4">
                  <Button variant="ghost" onClick={() => navigate('/articles')} className="gap-2" style={{ color: theme.text }}>
                    <ArrowRight className="w-4 h-4" /> المقالات
                  </Button>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" onClick={scrollToComments} className="lg:hidden gap-1.5" style={{ color: theme.text }} data-testid="scroll-to-comments-btn">
                      <MessageCircle className="w-4 h-4" />
                      <span className="text-sm">إضافة تعليق</span>
                    </Button>
                  </div>
                </div>

                {/* Font size controls */}
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-medium" style={{ color: theme.text, opacity: 0.6 }}>حجم الخط</span>
                    <div className="flex items-center gap-1.5 rounded-lg p-1" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' }}>
                      <button onClick={() => setFontSize(Math.max(14, fontSize - 2))} className="w-7 h-7 rounded-md flex items-center justify-center hover:bg-black/10 transition-colors" style={{ color: theme.text }} data-testid="decrease-font-btn">
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="text-xs font-bold w-7 text-center" style={{ color: theme.text }}>{fontSize}</span>
                      <button onClick={() => setFontSize(Math.min(28, fontSize + 2))} className="w-7 h-7 rounded-md flex items-center justify-center hover:bg-black/10 transition-colors" style={{ color: theme.text }} data-testid="increase-font-btn">
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="relative">
                    <Button variant="ghost" onClick={() => setShowThemePicker(!showThemePicker)} size="icon" style={{ color: theme.text }} data-testid="theme-picker-btn">
                      <Palette className="w-5 h-5" />
                    </Button>
                    {showThemePicker && (
                      <div className="absolute left-0 top-full mt-1 flex gap-2 p-2 rounded-xl border shadow-lg z-10" style={{ backgroundColor: isDark ? '#222' : '#fff', borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' }}>
                        {themes.map((t, i) => (
                          <button key={i} onClick={() => { setThemeIdx(i); setShowThemePicker(false); }}
                            className={`w-8 h-8 rounded-full border-2 transition-all ${i === themeIdx ? 'scale-110 ring-2 ring-primary ring-offset-1' : ''}`}
                            style={{ backgroundColor: t.bg, borderColor: i === themeIdx ? '#C19A6B' : isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)' }}
                            title={t.name}
                            data-testid={`theme-option-${i}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Title & meta */}
                <h1 className="text-2xl sm:text-3xl font-bold mb-4 leading-relaxed" style={{ color: theme.text }}>{article.title}</h1>
                <div className="flex flex-wrap gap-4 text-sm opacity-60" style={{ color: theme.text }}>
                  {article.author && <span className="flex items-center gap-1"><User className="w-4 h-4" /> {article.author}</span>}
                  {article.date && <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {new Date(article.date).toLocaleDateString('ar')}</span>}
                  <span className="flex items-center gap-1"><Eye className="w-4 h-4" /> {article.views} مشاهدة</span>
                  <span className="flex items-center gap-1"><MessageCircle className="w-4 h-4" /> {comments.length} تعليق</span>
                </div>
              </div>

              {/* Content */}
              {article.pdf_url ? (
                <div className="p-4">
                  {/* Desktop: native iframe, Mobile: Google Docs viewer */}
                  <iframe
                    src={article.pdf_url}
                    className="w-full rounded-lg hidden sm:block"
                    style={{ height: '80vh' }}
                    title={article.title}
                  />
                  <iframe
                    src={getPdfViewerUrl(article.pdf_url)}
                    className="w-full rounded-lg sm:hidden"
                    style={{ height: '80vh' }}
                    title={article.title}
                    sandbox="allow-scripts allow-same-origin allow-popups"
                  />
                </div>
              ) : (
                <div className="p-6 sm:p-8" style={{ fontSize: `${fontSize}px`, lineHeight: '2', color: theme.text }}>
                  <div className="article-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content) }} />
                </div>
              )}

              <div ref={articleEndRef} />
            </div>

            {/* Bottom Comments */}
            <div ref={bottomRef} className="rounded-2xl overflow-hidden shadow-sm mt-6 p-6 sm:p-8" style={{ backgroundColor: theme.surface }} data-testid="comments-section">
              <h2 className="text-xl font-bold mb-6 flex items-center gap-2" style={{ color: theme.text }}>
                <MessageCircle className="w-5 h-5" /> التعليقات ({comments.length})
              </h2>
              <div className="mb-6"><CommentForm compact={false} /></div>
              <div className="space-y-3">
                {comments.map(c => <CommentItem key={c.id} c={c} />)}
                {comments.length === 0 && <p className="text-center py-6 opacity-40 text-sm" style={{ color: theme.text }}>لا توجد تعليقات بعد. كن أول من يعلق!</p>}
              </div>
            </div>

            <div className="py-6 text-center">
              <Button variant="outline" onClick={() => navigate('/articles')} className="gap-2" style={{ color: theme.text, borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)' }} data-testid="back-to-articles">
                <ArrowRight className="w-4 h-4" /> العودة للمقالات
              </Button>
            </div>
          </div>

          {/* Desktop Sidebar - always rendered, animated */}
          <div
            className="hidden lg:block shrink-0 sticky top-4 self-start overflow-hidden"
            style={{
              width: sidebarVisible ? '320px' : '0px',
              opacity: sidebarVisible ? 1 : 0,
              transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease',
              maxHeight: 'calc(100vh - 2rem)',
            }}
            data-testid="comments-sidebar"
          >
            <div className="rounded-2xl overflow-hidden shadow-sm h-full flex flex-col w-80" style={{ backgroundColor: theme.surface, maxHeight: 'calc(100vh - 2rem)' }}>
              <div className="p-4 border-b" style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
                <h3 className="font-bold flex items-center gap-2 text-sm" style={{ color: theme.text }}>
                  <MessageCircle className="w-4 h-4 text-primary" /> التعليقات ({comments.length})
                </h3>
              </div>
              <div className="p-3"><CommentForm compact={true} /></div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2.5" style={{ scrollbarWidth: 'thin' }}>
                {comments.map(c => <CommentItem key={c.id} c={c} />)}
                {comments.length === 0 && <p className="text-center py-8 opacity-40 text-xs" style={{ color: theme.text }}>لا توجد تعليقات بعد</p>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleReadPage;
